import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Folder, FolderOpen, Search } from 'lucide-react';
import * as CollectionsModule from 'modules/collections';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import { CollectionFormModal } from 'pages/Library/CollectionFormModal';
import { SHARE_META } from 'pages/Library/CollectionCard';
import { LoadMore } from 'components/ui/pagination';
import { useSearchParamState } from 'hooks/use-search-param-state';
import {
  MobileScreen,
  ScreenHeader,
  HeaderIcon,
  HeaderPlus,
  ScreenBody,
  SearchInput,
  Segmented,
  ListCard,
  MobileSkeleton,
  MobileEmpty,
  MobileError,
  M,
} from 'components/mobile';

type LibraryTab = 'owned' | 'shared';

/** Collections revealed per "load more" — both lists arrive whole (the API isn't paged). */
const STEP = 12;

/** One card of collection rows; the segmented control swaps owned vs shared-with-me, the header icon opens search. */
export function LibraryMobile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tab, setTab] = useState<LibraryTab>('owned');
  const [createOpen, setCreateOpen] = useState(false);
  const { input: search, setInput: setSearch } = useSearchParamState();
  // Search hides behind the header's icon; a query keeps the field open.
  const [searchOpen, setSearchOpen] = useState(false);
  const showSearch = searchOpen || search !== '';
  const [shown, setShown] = useState(STEP);
  const canCreate = usePermission(PERMISSIONS.COLLECTIONS_CREATE);

  const owned = CollectionsModule.Hooks.useOwned();
  const shared = CollectionsModule.Hooks.useSharedWithMe();
  const { collections, isLoading, isError, refetch } = tab === 'owned' ? owned : shared;
  // The list DTO has no share scope: owned reads "Private", shared-with-me "Group".
  const shareLabel = t(SHARE_META[tab === 'owned' ? 'private' : 'team'].labelKey);

  const needle = search.trim().toLocaleLowerCase();
  const visible = useMemo(
    () => (needle ? collections.filter((c) => c.name.toLocaleLowerCase().includes(needle)) : collections),
    [collections, needle],
  );

  const changeSearch = (value: string) => {
    setSearch(value);
    setShown(STEP);
  };
  const toggleSearch = () => {
    if (showSearch) {
      changeSearch('');
      setSearchOpen(false);
    } else {
      setSearchOpen(true);
    }
  };

  const tabs = [
    { value: 'owned', label: t('library.all_collections') },
    { value: 'shared', label: t('library.shared_with_me') },
  ];

  const renderBody = () => {
    if (isLoading) return <MobileSkeleton />;
    if (isError) return <MobileError onRetry={() => refetch()} />;
    if (collections.length === 0) {
      return (
        <MobileEmpty
          icon={<FolderOpen size={22} />}
          title={t('library.no_collections_title')}
          description={tab === 'shared' ? t('library.nothing_shared_desc') : undefined}
        />
      );
    }
    if (visible.length === 0) {
      return (
        <MobileEmpty
          icon={<Search size={22} />}
          title={t('collections.none_match')}
          description={t('try_adjusting_your_search_or_filters')}
          action={
            <button
              type="button"
              onClick={() => changeSearch('')}
              className="h-10 rounded-md px-4 text-[14px] font-medium"
              style={{ background: M.card, border: `1px solid ${M.border}`, color: M.ink }}
            >
              {t('clear_search')}
            </button>
          }
        />
      );
    }
    return (
      <ScreenBody>
        <ListCard>
          {visible.slice(0, shown).map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => navigate(`/app/collections/${c.id}`)}
              className="flex w-full items-center gap-3 px-3.5 py-[13px] text-left"
            >
              <Folder className="h-5 w-5 flex-shrink-0" style={{ color: M.ink2 }} aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[15px] font-medium leading-[18px]" style={{ color: M.ink }}>
                  {c.name}
                </span>
                <span className="mt-0.5 block truncate text-[12.5px] leading-4 tabular-nums" style={{ color: M.ink3 }}>
                  {t('collections.items_count', { count: c.itemCount })} · {shareLabel}
                </span>
              </span>
              <ChevronRight className="h-4 w-4 flex-shrink-0" style={{ color: M.chevron }} aria-hidden />
            </button>
          ))}
        </ListCard>
        <LoadMore
          compact
          className="mt-3"
          shown={Math.min(shown, visible.length)}
          total={visible.length}
          step={STEP}
          onLoadMore={() => setShown((n) => n + STEP)}
        />
      </ScreenBody>
    );
  };

  return (
    <MobileScreen>
      <ScreenHeader
        title={t('library.title')}
        count={isLoading ? undefined : collections.length}
        right={
          <>
            <HeaderIcon
              icon={<Search size={19} />}
              label={t('collections.search_collections')}
              pressed={showSearch}
              onClick={toggleSearch}
            />
            {canCreate && <HeaderPlus onClick={() => setCreateOpen(true)} label={t('library.new_collection')} />}
          </>
        }
      >
        {showSearch && (
          <div className="pt-3">
            <SearchInput
              autoFocus
              value={search}
              onChange={changeSearch}
              placeholder={t('collections.search_collections')}
            />
          </div>
        )}
        <div className="pb-3 pt-3">
          <Segmented
            full
            aria-label={t('library.title')}
            tabs={tabs}
            value={tab}
            onChange={(v) => {
              setTab(v as LibraryTab);
              setShown(STEP);
            }}
          />
        </div>
      </ScreenHeader>

      {renderBody()}

      <CollectionFormModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        collection={null}
        onSuccess={() => setCreateOpen(false)}
      />
    </MobileScreen>
  );
}

export default LibraryMobile;
